import type { CapacityPeriod, Granularity, UtilisationStatus } from './types'

export function getGranularityLabel(g: Granularity): string {
  return { weekly: 'Weekly', monthly: 'Monthly' }[g]
}

export function getPeriodUnit(g: Granularity): string {
  return { weekly: 'Week', monthly: 'Month' }[g]
}

export function getPeriodStartIso(date: Date, g: Granularity): string {
  return formatIsoDate(startOfPeriod(date, g))
}

export function normalizePeriodStartIso(dateIso: string, g: Granularity): string {
  return getPeriodStartIso(new Date(`${dateIso}T00:00:00`), g)
}

export function buildPeriodStarts(fromIso: string, count: number, g: Granularity): string[] {
  const normalizedFromIso = normalizePeriodStartIso(fromIso, g)
  return Array.from({ length: count }, (_, i) =>
    formatIsoDate(addPeriods(new Date(`${normalizedFromIso}T00:00:00`), g, i))
  )
}

export function getWeeksInPeriod(periodStartIso: string, g: Granularity): string[] {
  const normalizedPeriodStartIso = normalizePeriodStartIso(periodStartIso, g)
  if (g === 'weekly') return [normalizedPeriodStartIso]
  const start = new Date(`${normalizedPeriodStartIso}T00:00:00`)
  const nextPeriodStart = addPeriods(new Date(start), g, 1)
  const weeks: string[] = []
  let monday = toMonday(start)
  while (monday < nextPeriodStart) {
    weeks.push(formatIsoDate(monday))
    monday = new Date(monday)
    monday.setDate(monday.getDate() + 7)
  }
  return weeks
}

export function getPeriodRangeEnd(fromIso: string, count: number, g: Granularity): string {
  const normalizedFromIso = normalizePeriodStartIso(fromIso, g)
  const end = addPeriods(new Date(`${normalizedFromIso}T00:00:00`), g, count)
  end.setDate(end.getDate() - 1)
  return formatIsoDate(end)
}

export function formatAllocationPeriodLabel(periodStartIso: string, g: Granularity): string {
  const normalizedPeriodStartIso = normalizePeriodStartIso(periodStartIso, g)
  const start = new Date(`${normalizedPeriodStartIso}T00:00:00`)
  if (g === 'monthly') return start.toLocaleString('default', { month: 'short' })

  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  return formatPeriodDateRange(start, end)
}

export function formatCapacityPeriodLabel(period: CapacityPeriod, g: Granularity): string {
  if (period.periodStart && period.periodEnd) {
    return formatPeriodDateRange(
      new Date(`${period.periodStart}T00:00:00`),
      new Date(`${period.periodEnd}T00:00:00`)
    )
  }

  return formatAllocationPeriodLabel(period.weekStart, g)
}

function toMonday(date: Date): Date {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  const diff = (result.getDay() + 6) % 7
  result.setDate(result.getDate() - diff)
  return result
}

export function getUtilisationStatus(pct: number): UtilisationStatus {
  if (pct >= 100) return 'over-allocated'
  if (pct >= 95) return 'burnout-risk'
  if (pct >= 85) return 'near-capacity'
  if (pct >= 70) return 'optimal'
  return 'under-utilised'
}

export function getStatusColour(status: string): string {
  const map: Record<string, string> = {
    'optimal': 'success',
    'near-capacity': 'warning',
    'over-allocated': 'error',
    'under-utilised': 'default',
    'burnout-risk': 'error',
    unavailable: 'default',
  }
  return map[status] ?? 'default'
}

export function formatIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function getCurrentRange(): { startDate: string; endDate: string } {
  const today = new Date()
  const end = new Date(today)
  end.setHours(0, 0, 0, 0)
  const start = new Date(today.getFullYear(), today.getMonth(), 1)

  return { startDate: formatIsoDate(start), endDate: formatIsoDate(end) }
}

export function shiftRange(
  startDate: string,
  granularity: Granularity,
  direction: -1 | 1
): { startDate: string; endDate: string } {
  const normalizedStartDate = normalizePeriodStartIso(startDate, granularity)
  const start = addPeriods(new Date(`${normalizedStartDate}T00:00:00`), granularity, direction)
  const end = addPeriods(new Date(start), granularity, 6)
  end.setDate(end.getDate() - 1)

  return { startDate: formatIsoDate(start), endDate: formatIsoDate(end) }
}

export function formatRangeLabel(startDate: string, endDate: string): string {
  const formatter = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  return `${formatter.format(new Date(`${startDate}T00:00:00`))} - ${formatter.format(new Date(`${endDate}T00:00:00`))}`
}

function startOfPeriod(date: Date, granularity: Granularity): Date {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)

  if (granularity === 'weekly') {
    const diff = (result.getDay() + 6) % 7
    result.setDate(result.getDate() - diff)
    return result
  }

  result.setDate(1)
  return result
}

function addPeriods(date: Date, granularity: Granularity, count: number): Date {
  const result = new Date(date)
  if (granularity === 'weekly') result.setDate(result.getDate() + 7 * count)
  if (granularity === 'monthly') result.setMonth(result.getMonth() + count)
  return result
}

function formatPeriodDateRange(start: Date, end: Date): string {
  const monthFormatter = new Intl.DateTimeFormat(undefined, { month: 'short' })
  const startMonth = monthFormatter.format(start)
  const endMonth = monthFormatter.format(end)
  const startDay = start.getDate()
  const endDay = end.getDate()
  const startYear = start.getFullYear()
  const endYear = end.getFullYear()

  if (startYear !== endYear) {
    return `${startMonth} ${startDay}, ${startYear}-${endMonth} ${endDay}, ${endYear}`
  }

  if (start.getMonth() !== end.getMonth()) {
    return `${startMonth} ${startDay}-${endMonth} ${endDay}`
  }

  return `${startMonth} ${startDay}-${endDay}`
}
